import { useMemo, useRef, useState } from 'react'
import { History, Loader2, Plus, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { createTickerSearchMatcher } from '@/lib/tickerSearch'
import { CopyTickerButton } from './CopyTickerButton'
import type { ScreenerTicker } from '@/types'

interface ScreenerHistoryPanelProps {
  tickers: ScreenerTicker[]
  activeTicker: string | null
  onSelectTicker: (symbol: string) => void
  onAddTicker: (symbol: string) => Promise<boolean>
  onRemoveTicker: (symbol: string) => Promise<boolean>
  isLoading: boolean
}

export function ScreenerHistoryPanel({
  tickers,
  activeTicker,
  onSelectTicker,
  onAddTicker,
  onRemoveTicker,
  isLoading,
}: ScreenerHistoryPanelProps) {
  const [search, setSearch] = useState('')
  const [newSymbol, setNewSymbol] = useState('')
  const [adding, setAdding] = useState(false)
  const [removingSymbol, setRemovingSymbol] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const addPending = useRef(false)
  const removePending = useRef(false)
  const visibleTickers = useMemo(() => {
    const matchesSearch = createTickerSearchMatcher(search, tickers.map((ticker) => ticker.symbol))
    return tickers.filter((ticker) => matchesSearch(ticker.symbol))
  }, [tickers, search])

  const handleAdd = async () => {
    const symbol = newSymbol.trim().toUpperCase()
    if (!symbol || isLoading || addPending.current) return

    addPending.current = true
    setAdding(true)
    setError(null)
    try {
      if (await onAddTicker(symbol)) {
        setNewSymbol('')
        setSearch('')
      } else {
        setError(`Could not add ${symbol}. Please try again.`)
      }
    } catch {
      setError(`Could not add ${symbol}. Please try again.`)
    } finally {
      addPending.current = false
      setAdding(false)
    }
  }

  const handleRemove = async (symbol: string) => {
    if (isLoading || removePending.current) return

    removePending.current = true
    setRemovingSymbol(symbol)
    setError(null)
    try {
      if (!await onRemoveTicker(symbol)) {
        setError(`Could not remove ${symbol}. Please try again.`)
      }
    } catch {
      setError(`Could not remove ${symbol}. Please try again.`)
    } finally {
      removePending.current = false
      setRemovingSymbol(null)
    }
  }

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col bg-[#080d14]">
      <div className="shrink-0 border-b border-slate-800 p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-600" aria-hidden="true" />
          <Input
            aria-label="Search ticker history"
            placeholder="Search history..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="h-8 border-slate-800 bg-slate-950/60 pl-8 pr-8 text-xs text-slate-200 placeholder:text-slate-600"
          />
          {search && (
            <button
              type="button"
              aria-label="Clear history search"
              onClick={() => setSearch('')}
              className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-500 hover:text-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400"
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="mt-2 text-[10px] text-slate-500" aria-live="polite">
          {search.trim() ? `${visibleTickers.length} of ${tickers.length}` : tickers.length} ticker{tickers.length === 1 ? '' : 's'}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto" aria-label="Ticker history" aria-busy={isLoading}>
        {visibleTickers.map((ticker) => {
          const active = ticker.symbol === activeTicker
          const exchangeCount = ticker.available_exchanges.length
          return (
            <div
              key={ticker.symbol}
              className={cn(
                'flex items-center border-b border-slate-800/65',
                active ? 'bg-teal-400/10' : 'hover:bg-slate-900/80',
              )}
            >
              <button
                type="button"
                onClick={() => onSelectTicker(ticker.symbol)}
                aria-pressed={active}
                title={ticker.symbol}
                className="flex min-w-0 flex-1 items-center justify-between gap-3 px-3 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-400"
              >
                <span className={cn('truncate text-xs font-semibold', active ? 'text-teal-300' : 'text-slate-100')}>
                  {ticker.symbol}
                </span>
                <span className="shrink-0 text-[10px] text-slate-500">
                  {exchangeCount} venue{exchangeCount === 1 ? '' : 's'}
                </span>
              </button>
              <CopyTickerButton symbol={ticker.symbol} />
              <button
                type="button"
                aria-label={`Remove ${ticker.symbol} from history`}
                title={`Remove ${ticker.symbol} from history`}
                onClick={() => void handleRemove(ticker.symbol)}
                disabled={isLoading || removingSymbol !== null}
                className="mr-2 shrink-0 rounded p-2 text-slate-500 hover:bg-rose-400/10 hover:text-rose-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 disabled:opacity-40"
              >
                {removingSymbol === ticker.symbol
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  : <X className="h-3.5 w-3.5" aria-hidden="true" />}
              </button>
            </div>
          )
        })}
        {visibleTickers.length === 0 && (
          <div className="flex flex-col items-center gap-2 px-5 py-10 text-center text-xs text-slate-500" role="status">
            {isLoading
              ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              : <History className="h-5 w-5 text-slate-600" aria-hidden="true" />}
            <span>
              {isLoading ? 'Loading history...' : tickers.length ? 'No tickers match your search.' : 'No tickers in history yet.'}
            </span>
            {!isLoading && tickers.length === 0 && (
              <span className="text-[10px] text-slate-600">Open a ticker from Discovery or add one below.</span>
            )}
          </div>
        )}
      </div>

      <form
        className="shrink-0 border-t border-slate-800 p-3"
        onSubmit={(event) => {
          event.preventDefault()
          void handleAdd()
        }}
      >
        {error && <div className="mb-2 text-xs text-rose-400" role="alert">{error}</div>}
        <div className="flex gap-2">
          <Input
            aria-label="Add ticker to history"
            placeholder="Add ticker (e.g. ETH)"
            value={newSymbol}
            onChange={(event) => setNewSymbol(event.target.value.toUpperCase())}
            disabled={isLoading || adding}
            className="h-8 min-w-0 border-slate-800 bg-slate-950/60 text-xs text-slate-200 placeholder:text-slate-600"
          />
          <Button
            type="submit"
            variant="outline"
            size="icon"
            aria-label={adding ? 'Adding ticker' : 'Add ticker'}
            disabled={isLoading || adding || !newSymbol.trim()}
            className="h-8 w-8 shrink-0 border-slate-800 text-teal-300 hover:bg-teal-400/10 hover:text-teal-200"
          >
            {adding
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              : <Plus className="h-3.5 w-3.5" aria-hidden="true" />}
          </Button>
        </div>
      </form>
    </div>
  )
}
